import React, { FC } from "react";
import { Autocomplete, Box, Chip, InputLabel, ListItemIcon, TextField } from "@mui/material";
import "./styles.css";
import { ErrorMessageBox } from "../errorMessage";
import { KeyboardArrowDown } from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import GCTooltip from "../infoToolTip";
import LockIcon from "@mui/icons-material/Lock";
import { Status } from "../../enum";

export type GClistOptionContent = {
    value: string | any;
    label: string;
    lockStatus?: string;
    routeNumber?: string;
};

type GCAutocompleteProps =
    | {
        id?: string;
        label?: string;
        labelPlacement?: "inner" | "outer";
        placeholder?: string;
        multiSelect?: true;
        options: GClistOptionContent[];
        defaultValue?: any;
        value?: any;
        onChange: Function;
        errorMessage?: string;
        refetchFunction?: Function;
        setBlur?: Function;
        required?: boolean;
        prefix?: any;
        disableClearable?: boolean;
        nonRenderTags?: string[];
        direction?: "ltr" | "rtl";
        disabled?: boolean;
        loading?: boolean;
        information?: any;
        size?: "small" | "medium" | "extra-small";
        chipMaxWidth?: number;
        onFocus?: any;
        getOptionDisabled?: (option: GClistOptionContent) => boolean;
    }
    | {
        id?: string;
        options: GClistOptionContent[];
        onChange: Function;
        label?: string;
        labelPlacement?: "inner" | "outer";
        placeholder?: string;
        multiSelect?: false;
        defaultValue?: any;
        value?: any;
        errorMessage?: string;
        refetchFunction?: Function;
        setBlur?: Function;
        required?: boolean;
        prefix?: any;
        disableClearable?: boolean;
        nonRenderTags?: string[];
        direction?: "ltr" | "rtl";
        disabled?: boolean;
        loading?: boolean;
        information?: any;
        size?: "small" | "medium" | "extra-small";
        chipMaxWidth?: number;
        onFocus?: any;
        getOptionDisabled?: (option: GClistOptionContent) => boolean;
    };

export const GCAutocomplete: FC<GCAutocompleteProps> = ({
    id,
    label,
    labelPlacement = "outer",
    placeholder,
    disableClearable = false,
    multiSelect = false,
    options,
    onChange,
    value,
    errorMessage,
    defaultValue,
    refetchFunction = () => { },
    setBlur = () => { },
    required,
    prefix,
    nonRenderTags = [],
    direction = "ltr",
    disabled = false,
    loading = false,
    information,
    size = "small",
    chipMaxWidth = 150,
    onFocus,
    getOptionDisabled
}) => {
    const SearchChangeFunction = debounce((searchValue: string) => {
        refetchFunction(searchValue?.toLowerCase());
    }, 1000);

    function debounce(callback: any, delay = 1000) {
        let time: any;
        return (...args: any) => {
            clearTimeout(time);
            time = setTimeout(() => {
                callback(...args);
            }, delay);
        };
    }

    const isRender = (value: string) => {
        return !nonRenderTags.includes(value);
    };

    // const autoCompleteLabel = `${required ? " * " : ""}${label}`;
    const autoCompleteLabel = (
        <>
            {required && <span className="required"> * </span>}
            {label}
        </>
    );

    return (
        <Box className="inputContainer">
            {label && labelPlacement === "outer" && (
                <Box display={"flex"} alignItems={"center"}>
                    <InputLabel>{autoCompleteLabel}</InputLabel>
                    {information && (
                        <GCTooltip title={information} placement="right" arrow>
                            <InfoOutlinedIcon fontSize="small" className="informationIcon" />
                        </GCTooltip>
                    )}
                </Box>
            )}
            <Autocomplete
                disableClearable={disableClearable}
                multiple={multiSelect}
                loading={loading}
                disableCloseOnSelect={multiSelect}
                onChange={(event: React.SyntheticEvent, value: any) => {
                    setBlur();
                    onChange(value);
                }}
                onFocus={onFocus}
                disabled={disabled}
                onInputChange={(
                    event: React.SyntheticEvent,
                    value: string,
                    reason: string
                ) => {
                    
                    if (reason !== "reset") SearchChangeFunction(value);
                }}
                defaultValue={defaultValue}
                value={value}
                onBlur={() => {
                    if (options?.length === 0) refetchFunction("");
                }}
                options={options ?? []}
                getOptionLabel={(option) => `${option?.label}`}
                isOptionEqualToValue={(option, val) => option?.value === val?.value}
                getOptionDisabled={getOptionDisabled}
                renderOption={(props, option) => (
                    <li
                        className="renderOptionAutoComplete"
                        style={{
                            direction,
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                        }}
                        {...props}
                    >
                        {option?.label}
                        {option?.lockStatus === Status?.Active && (
                            <ListItemIcon sx={{ minWidth: "24px", color: "gray",gap: "8px" }}>
                                <LockIcon fontSize="small" />
                                Route Number: #{option?.routeNumber}
                            </ListItemIcon>
                            
                        )}

                    </li>
                )}
                popupIcon={<KeyboardArrowDown />}
                renderTags={(value, getTagProps) =>
                    value.map(
                        (option: any, index: number) =>
                            isRender(option?.value) && (
                                <Chip
                                    label={option?.label}
                                    {...getTagProps({ index })}
                                    className="chipAutocomplete"
                                    size="small"
                                    sx={{ maxWidth: chipMaxWidth }}
                                />
                            )
                    )
                }
                renderInput={(params: any) => {
                    return (
                        <GCTooltip
                            title={
                                params?.inputProps?.ref?.current?.scrollWidth >
                                    params?.inputProps?.ref?.current?.clientWidth
                                    ? params?.inputProps?.value
                                    : ""
                            }
                            placement="top"
                            arrow
                        >
                            <TextField
                                {...params}
                                className="textAutoComplete"
                                variant="outlined"
                                size={size !== "extra-small" ? size : "small"}
                                error={errorMessage ? true : false}
                                label={
                                    label && labelPlacement === "inner" ? autoCompleteLabel : null
                                }
                                placeholder={placeholder}
                                InputProps={
                                    multiSelect
                                        ? {
                                            ...params.InputProps,
                                        }
                                        : {
                                            ...params.InputProps,
                                            startAdornment: prefix && (
                                                <Chip
                                                    tabIndex={-1}
                                                    label={prefix}
                                                    className="adornment-chip"
                                                />
                                            ),
                                        }
                                }
                            />
                        </GCTooltip>
                    );
                }}
                className="gsAutoComplete"
                sx={
                    size === "extra-small"
                        ? {
                            ".MuiAutocomplete-input": {
                                height: "15px !important",
                            },
                        }
                        : {}
                }
            />
            {errorMessage !== undefined && (
                <ErrorMessageBox message={errorMessage ?? null} />
            )}
        </Box>
    );
};
